/**
 * Standardized date formatting utilities for consistent UTC timezone handling
 * across the rebate forecasting system.
 */

import { isDateOnlyString, parseDateOnlyLocal } from '@erp/shared-utils';

/**
 * Format a date for display with consistent UTC timezone
 */
export const formatDateDisplay = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(dateObj);
};

/**
 * Format a date for full display with day of week
 */
export const formatFullDateDisplay = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(dateObj);
};

export function formatDatetime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(dateObj);
}

/** Local date + time with seconds (e.g. list views, audit-style timestamps). */
export function formatDatetimeWithSeconds(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(dateObj);
}

/**
 * Format a date for month display only
 * When multiple years are present, includes year in the format
 */
export const formatMonthDisplay = (
  date: Date | string,
  includeYear?: boolean,
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  }).format(dateObj);
};

/**
 * Format a date for month name only
 */
export const formatMonthName = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    timeZone: 'UTC',
  }).format(dateObj);
};

/**
 * Format a date for payment/invoice tables
 */
export const formatPaymentDate = (dateString: string): string => {
  if (!dateString) return '-';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(dateString));
};

/**
 * Format a date for manual amount entries
 */
export const formatManualAmountDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    timeZone: 'UTC',
  });
};

/**
 * Get the current year for consistent year-based calculations
 */
export const getCurrentYear = (): number => {
  return new Date().getUTCFullYear();
};

/**
 * Parse a date-only string (YYYY-MM-DD) in a timezone-safe way.
 * This avoids timezone shifts that occur when using `new Date(dateString)`
 * on date-only strings.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object or null if invalid
 */
export const parseDateOnly = (dateString: string): Date | null => {
  if (!dateString) return null;
  return parseDateOnlyLocal(dateString);
};

/**
 * Format a date-only string (YYYY-MM-DD) for display without timezone shifts.
 * Falls back to the original string if parsing fails.
 *
 * @param dateString - Date string in YYYY-MM-DD format or ISO timestamp
 * @param fallback - Optional fallback value (defaults to 'N/A')
 * @returns Formatted date string like "Jan 15, 2024"
 */
export const formatDateOnlyString = (
  dateString: string | undefined,
  fallback = 'N/A',
): string => {
  if (!dateString) return fallback;

  if (isDateOnlyString(dateString)) {
    const date = parseDateOnlyLocal(dateString);
    if (!date) return fallback;

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  }

  // If parseDateOnly fails, attempt to parse as a regular Date (handles ISO timestamps)
  const dt = new Date(dateString);
  if (!isNaN(dt.getTime())) {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(dt);
  }

  // If all parsing fails, return the fallback
  return fallback;
};

/**
 * Check if a date string is valid
 */
export const isValidDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Create a date range string for display
 */
export const formatDateRange = (startDate: string, endDate: string): string => {
  const start = formatDateDisplay(startDate);
  const end = formatDateDisplay(endDate);
  return `${start} - ${end}`;
};
